import { Suspense } from "react";
import {
  ResponsiveContainer,
  Treemap,
  Tooltip as RechartsTooltip,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart as PieChartIcon } from "lucide-react";
import { EcosuiteErrorBoundary } from "@/components/error-boundary";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { UserCounts } from "@/features-root-user/service/organization";
import { CHART_COLORS } from "../constants";

type UserTypeChartData = {
  name: string;
  size: number;
  color: string;
  users: any[];
};

interface UserTypeChartProps {
  userTypeChartData: UserTypeChartData[];
  userCounts?: UserCounts;
}

// Custom tooltip for treemap
const CustomTreemapTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const totalUsers = data.totalUsers || 0;
    const percentage =
      totalUsers > 0 ? ((data.size / totalUsers) * 100).toFixed(1) : "0";

    return (
      <div className="bg-background border-border rounded-md border p-2 text-xs shadow-sm">
        <p className="font-medium">{data.name}</p>
        <p className="text-muted-foreground mt-1">Users: {data.size}</p>
        <p className="text-muted-foreground">Percentage: {percentage}%</p>
      </div>
    );
  }
  return null;
};

// Custom content for treemap
const CustomTreemapContent = (props: any) => {
  const { x, y, width, height, name, size, color } = props;

  // Determine if we should show text based on rectangle size
  // More conservative values to prevent cutoff
  const shouldShowText = width > 60 && height > 40;
  const shouldShowCount = width > 90 && height > 50;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: "#fff",
          strokeWidth: 2,
          strokeOpacity: 1,
        }}
      />
      {shouldShowText && (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          fill="#fff"
          fontSize={10}
          fontWeight="normal"
          dominantBaseline="middle"
        >
          <tspan x={x + width / 2} dy="-0.5em">
            {name}
          </tspan>
          {shouldShowCount && (
            <tspan x={x + width / 2} dy="1.4em">
              {size}
            </tspan>
          )}
        </text>
      )}
    </g>
  );
};

function UserTypeChartContent({
  userTypeChartData,
  userCounts,
}: UserTypeChartProps) {
  // Add totalUsers to each data point for percentage calculation in tooltip
  const enhancedChartData = userTypeChartData.map((item) => ({
    ...item,
    totalUsers: userCounts?.total || 0,
  }));

  return (
    <Card className="border-muted/50 overflow-hidden shadow-sm">
      <CardHeader className="bg-muted/10 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <PieChartIcon className="text-primary h-4 w-4" />
            <CardTitle className="text-base">User Type Distribution</CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="h-72 pt-0">
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={enhancedChartData}
            dataKey="size"
            aspectRatio={4 / 3}
            stroke="#fff"
            content={<CustomTreemapContent />}
            animationDuration={500}
          >
            <RechartsTooltip content={<CustomTreemapTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export function UserTypeChart(props: UserTypeChartProps) {
  return (
    <EcosuiteErrorBoundary>
      <Suspense
        fallback={<LoadingScreen text="Loading chart..." className="h-72" />}
      >
        <UserTypeChartContent {...props} />
      </Suspense>
    </EcosuiteErrorBoundary>
  );
}
